<template>
  <Card>
    <template #title="">
      <div class="p-d-flex p-justify-between">
        ชื่อหลักสูตร
        <div>
          <Button
            label="สร้างใหม่"
            icon="pi pi-plus"
            class="p-ml-2"
            @click="onNewCourse"
          ></Button>
          <back-button class="p-ml-2" />
        </div>
      </div>
    </template>
    <template #content="">
      <div class="p-grid">
        <div class="p-col-12">
          <div class="p-grid">
            <div class="p-col-12 p-lg-4 p-md-6 p-fluid">
              <div class="p-inputgroup">
                <span class="p-inputgroup-addon">
                  <i class="pi pi-search"></i>
                </span>
                <InputText
                  v-model="searchString"
                  placeholder="ระบุคำค้น"
                ></InputText>
              </div>
            </div>
          </div>
        </div>
        <div class="p-col-12">
          <DataTable
            :paginator="true"
            :rows="10"
            :rowsPerPageOptions="[10, 20, 50]"
            :value="filteredCourse"
            class="p-datatable-sm"
            ref="courseTable"
            dataKey="id"
          >
            <template #empty>ไม่พบข้อมูล</template>
            <Column field="id" header="รหัส" :sortable="true"> </Column>
            <Column
              field="description"
              header="Description"
              :sortable="true"
            ></Column>
            <Column
              field="courseTypeId"
              header="ประเภทหลักสูตร"
              :sortable="true"
            ></Column>
            <Column
              field="courseType.description"
              header="ประเภทหลักสูตร"
              :sortable="true"
            ></Column>
            <Column
              headerStyle="width: 10rem;"
              bodyStyle="width:10rem; text-align: right"
            >
              <template #body="props">
                <Button
                  class="p-button-rounded p-button-success p-mr-2"
                  icon="pi pi-pencil"
                  @click="onEditCourse(props.data)"
                ></Button>
                <Button
                  class="p-button-rounded p-button-warning"
                  icon="pi pi-trash"
                  @click="onDeleteCourse(props.data)"
                ></Button>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
  </Card>
  <Dialog
    v-model:visible="showDialog"
    :modal="true"
    class="p-fluid"
    header="เพิ่มหลักสูตรใหม่"
  >
    <div class="p-field">
      <label for="dropdownCourdeType">ประเภทหลักสูตร</label>
      <span class="p-float-label">
        <Dropdown
          id="dropdownCourdeType"
          v-model="course.courseTypeId"
          :options="courseTypes"
          optionLabel="description"
          optionValue="id"
          required="true"
          :breakpoints="{ '960px': '75vw', '640px': '100vw' }"
          :style="{ width: '50vw' }"
          appendTo="body"
          :filter="true"
          :class="{ 'p-invalid': v$.course.courseTypeId.$error }"
        >
          <template #value="props">
            <span v-if="props.value">
              {{ props.value }} -
              {{
                courseTypes.find((x) => x.id === props.value)?.description
              }}</span
            >
            <span v-else>&nbsp;</span>
          </template>
          <template #option="props">
            {{ props.option.id }} - {{ props.option.description }}
          </template>
        </Dropdown>
      </span>
      <small class="p-error" v-if="v$.course.courseTypeId.$error">
        {{ v$.course.courseTypeId.required.$message }}
      </small>
    </div>
    <div class="p-field">
      <label for="name">Description</label>
      <InputText
        id="name"
        v-model="course.description"
        maxlength="100"
        :class="{ 'p-invalid': v$.course.description.$error }"
      ></InputText>
      <small class="p-error" v-if="v$.course.description.$error">
        {{ v$.course.description.required.$message }}
      </small>
    </div>
    <template #footer>
      <Button
        icon="pi pi-check"
        label="ยืนยัน"
        class="p-button-success"
        @click="onSaveCourse"
      ></Button>
      <Button
        icon="pi pi-times"
        label="ยกเลิก"
        class="p-button-danger p-ml-2"
        @click="onHideDialog"
      ></Button>
    </template>
  </Dialog>
  <Dialog
    v-model:visible="popupDeleteDialog"
    :style="{ width: '450px' }"
    header="Confirm"
    :modal="true"
  >
    <div class="confirmation-content">
      <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem" />
      <span v-if="course"
        >Are you sure you want to delete <b>{{ course.id }}</b
        >?</span
      >
    </div>
    <template #footer>
      <Button
        label="No"
        icon="pi pi-times"
        class="p-button-text"
        @click="popupDeleteDialog = false"
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        class="p-button-text"
        @click="handleDeleteCourse"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { ref, onMounted, computed } from "vue";
import { usePlugin } from "@/plugins";
import { clone } from "ramda";
import {
  Course,
  GetCourseResponse,
  UpsertCourseRequest,
  GetCourseTypeResponse,
  CourseType,
  TrainingTypeEnum,
  GetCourseRequest,
  UpsertCourseResponse,
} from "@/types";
import Fuse from "fuse.js";
import BackButton from "@/components/BackButton.vue";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
export default {
  name: "Course",
  components: { BackButton },
  setup() {
    const { success, error, axios, setLoading, onRouterPush } = usePlugin();

    const courseTable = ref(null);
    const courses = ref<Course[]>([]);
    const course = ref<Course>({} as Course);
    const courseTypes = ref<CourseType[]>([]);
    const showDialog = ref(false);
    const popupDeleteDialog = ref(false);
    const searchString = ref("");
    const existCourseId = ref(false);
    const duplicateCourseId = ref(false);

    const filteredCourse = computed(() => {
      if (!searchString.value) return courses.value;
      const fuse = new Fuse(courses.value, {
        keys: ["courseId", "description"],
      });
      const searchResult = fuse.search(searchString.value);
      return searchResult.map((x) => x.item);
    });

    const loadCourses = async () => {
      setLoading(true);
      try {
        const result = await axios.post<GetCourseResponse>(
          "api/v1/services/course/get",
          {} as GetCourseRequest
        );
        courses.value = result.data.courses ?? [];
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };
    const loadCourseTypes = async () => {
      setLoading(true);
      try {
        const result = await axios.get<GetCourseTypeResponse>(
          "api/v1/services/courseType/get"
        );
        courseTypes.value = result.data.courseTypes ?? [];
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onNewCourse = () => {
      course.value = {
        id: "",
        description: null,
        courseTypeId: null,
        courseType: null,
      };
      v$.value.$reset();
      showDialog.value = true;
      existCourseId.value = false;
    };

    const rules = {
      course: {
        description: { required: required },
        courseTypeId: { required: required },
      },
    };
    const v$ = useVuelidate(rules, { course }, { $autoDirty: true });

    const onSaveCourse = async () => {
      v$.value.$touch();
      if (v$.value.$error) return;

      setLoading(true);
      try {
        const result = await axios.post<UpsertCourseResponse>(
          "api/v1/services/course/upsert",
          {
            courses: [
              {
                id: "",
                description: course.value.description,
                courseTypeId: course.value.courseTypeId,
              },
            ],
          } as UpsertCourseRequest
        );
        await loadCourses();
        success("บันทึกข้อมูลสำเร็จแล้ว");

        if (result.data.course?.id) {
          onRouterPush({
            name: "editCourse",
            params: { id: result.data.course.id },
          });
        }
        showDialog.value = false;
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
      // }
    };

    const onHideDialog = () => {
      showDialog.value = false;

      duplicateCourseId.value = false;
    };

    const onEditCourse = (source: Course) => {
      onRouterPush({ name: "editCourse", params: { id: source.id } });
    };

    const onDeleteCourse = (source: Course) => {
      course.value = clone(source);
      popupDeleteDialog.value = true;
    };

    const handleDeleteCourse = async () => {
      setLoading(true);
      try {
        const result = await axios.delete("api/v1/services/course/delete", {
          data: {
            courses: [course.value],
          } as UpsertCourseRequest,
        });
        await loadCourses();
        success("ลบข้อมูลสำเร็จแล้ว");
        popupDeleteDialog.value = false;
      } catch (e) {
        error(e);
        popupDeleteDialog.value = false;
      } finally {
        setLoading(false);
      }
      // }
    };

    onMounted(async () => {
      loadCourseTypes();
      await loadCourses();
    });
    return {
      courseTable,
      course,
      courses,
      filteredCourse,
      showDialog,
      existCourseId,
      duplicateCourseId,
      popupDeleteDialog,
      searchString,
      onNewCourse,
      onEditCourse,
      onDeleteCourse,
      onSaveCourse,
      handleDeleteCourse,
      onHideDialog,
      courseTypes,
      TrainingTypeEnum,
      v$,
    };
  },
};
</script>
